import React from 'react';
import { Box } from '@mui/material';
import { useStorefrontContext } from 'context/StorefrontContext';
import { resolveStoreMediaUrl } from 'utils/themeDisplay';
import {
  clampWatermarkOpacity,
  clampWatermarkSize,
  normalizeWatermarkPosition,
  ProductWatermarkPosition,
} from 'utils/productWatermark';

export interface ProductWatermarkSettings {
  url?: string;
  opacity?: number;
  position?: string;
  size?: number;
}

interface ProductWatermarkOverlayProps {
  /** false cuando el producto no tiene foto propia. */
  active?: boolean;
  /** Si se pasa, se usa en lugar del tema publicado (vista previa del admin). */
  settings?: ProductWatermarkSettings | null;
}

const placementSx = (position: ProductWatermarkPosition) => {
  const edge = '6%';
  switch (position) {
    case 'top-left':
      return { top: edge, left: edge };
    case 'top-right':
      return { top: edge, right: edge };
    case 'bottom-left':
      return { bottom: edge, left: edge };
    case 'bottom-right':
      return { bottom: edge, right: edge };
    default:
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }
};

/** Marca de agua sobre la foto. El contenedor debe ser position: relative. */
const ProductWatermarkOverlay: React.FC<ProductWatermarkOverlayProps> = ({ active = true, settings }) => {
  const { theme } = useStorefrontContext();
  const source = settings ?? {
    url: theme?.productWatermarkUrl,
    opacity: theme?.productWatermarkOpacity,
    position: theme?.productWatermarkPosition,
    size: theme?.productWatermarkSize,
  };
  const src = resolveStoreMediaUrl(source.url);
  if (!active || !src) return null;

  const position = normalizeWatermarkPosition(source.position);
  const size = clampWatermarkSize(source.size);

  return (
    <Box
      component="img"
      src={src}
      alt=""
      aria-hidden
      draggable={false}
      sx={{
        position: 'absolute',
        width: `${size}%`,
        height: `${size}%`,
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        opacity: clampWatermarkOpacity(source.opacity) / 100,
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 0,
        ...placementSx(position),
      }}
    />
  );
};

export default ProductWatermarkOverlay;
